/**
 * Pagina per aggiungere un nuovo Tenant (Agency)
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAgency, CreateAgencyData } from "../api/agencyApi";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import { useNotification } from "../context/NotificationContext";

const AddTenantPage: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  
  const [formData, setFormData] = useState<CreateAgencyData>({
    nome: "",
    descrizione: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validazione nome
    if (!formData.nome.trim()) {
      newErrors.nome = "Il nome è obbligatorio";
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = "Il nome deve essere di almeno 2 caratteri";
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = "Il nome non può superare i 100 caratteri";
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(formData.nome.trim())) {
      newErrors.nome = "Il nome può contenere solo lettere, numeri, spazi, trattini e underscore";
    }

    // Validazione descrizione
    if (!formData.descrizione.trim()) {
      newErrors.descrizione = "La descrizione è obbligatoria";
    } else if (formData.descrizione.trim().length > 500) {
      newErrors.descrizione = "La descrizione non può superare i 500 caratteri";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Rimuovi l'errore quando l'utente inizia a digitare
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await createAgency({
        nome: formData.nome.trim(),
        descrizione: formData.descrizione.trim()
      });

      if (response.success) {
        addNotification("success", "Tenant creato con successo!");
        navigate("/tenants");
      } else {
        addNotification("error", response.error || "Errore nella creazione del tenant");
      }
    } catch (error: any) {
      console.error("Errore nella creazione del tenant:", error);
      addNotification("error", error.message || "Errore nella creazione del tenant");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/tenants");
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title="Aggiungi Nuovo Tenant"
        subtitle="Crea un nuovo tenant (agency) per organizzare i contenuti"
      />

      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nome Tenant */}
          <div>
            <label 
              htmlFor="nome" 
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Nome Tenant <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="nome"
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.nome ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="es. aiautomation, studiapp"
              maxLength={100}
            />
            {errors.nome && (
              <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Usa solo lettere, numeri, trattini e underscore. Il nome sarà convertito in minuscolo.
            </p>
          </div>

          {/* Descrizione */}
          <div>
            <label 
              htmlFor="descrizione" 
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Descrizione <span className="text-red-500">*</span>
            </label>
            <textarea
              id="descrizione"
              name="descrizione"
              rows={4}
              value={formData.descrizione}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.descrizione ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Descrizione del tenant e dei suoi contenuti..."
              maxLength={500}
            />
            {errors.descrizione && (
              <p className="mt-1 text-sm text-red-600">{errors.descrizione}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              {formData.descrizione.length}/500 caratteri
            </p>
          </div>

          {/* Pulsanti */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
              onClick={() => {}}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creazione...
                </>
              ) : (
                "Crea Tenant"
              )}
            </Button>
            
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              Annulla
            </Button>
          </div>
        </form>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <span className="text-blue-600 text-lg">ℹ️</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-900">
              Informazioni sui Tenant
            </h3>
            <div className="mt-2 text-sm text-blue-800">
              <ul className="list-disc list-inside space-y-1">
                <li>I tenant permettono di organizzare contenuti per diversi clienti</li>
                <li>Il nome del tenant verrà utilizzato nelle API per identificare i contenuti</li>
                <li>Una volta creato, il tenant non può essere eliminato se ha contenuti associati</li>
                <li>I nomi sono case-insensitive e verranno convertiti in minuscolo</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTenantPage;